import React, { useState } from "react";
import { makeRequestAuth, uploadFiles } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { FiX, FiUploadCloud, FiImage } from "react-icons/fi";

export default function AddPracticeModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({ name: "", paper: "", durationMinutes: "" });
  const [icon, setIcon] = useState("");
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleIconChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingIcon(true);
      const res = await uploadFiles({ files: [file] });
      setIcon(res?.data?.imageUrl);
      showSuccessToast("Icon uploaded successfully");
    } catch (error) {
      showErrorToast(error.message || "Failed to upload image");
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.paper || !icon)
      return showErrorToast("All fields are required");

    try {
      setLoading(true);
      await makeRequestAuth("post", "/admin/addPractice", {
        name: formData.name,
        icon: icon,
        durationMinutes: Number(formData.durationMinutes),
        paper: Number(formData.paper),
      });
      showSuccessToast("Practice added successfully");
      onSuccess();
    } catch {
      showErrorToast("Failed to add practice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm transition-all">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden transform transition-all scale-100">
        {/* HEADER */}
        <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50">
          <h3 className="text-lg font-bold text-gray-800">Add New Practice</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-200 text-gray-500 transition"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* BODY */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* UPLOAD AREA */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700">
              Practice Icon
            </label>
            <div
              className={`relative border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center text-center transition-colors ${icon ? "border-blue-300 bg-blue-50/30" : "border-gray-300 hover:bg-gray-50"}`}
            >
              {icon ? (
                <div className="relative group">
                  <img
                    src={icon}
                    alt="Preview"
                    className="h-20 w-20 object-contain drop-shadow-sm"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded">
                    <span className="text-white text-xs">Change</span>
                  </div>
                </div>
              ) : (
                <div className="py-3">
                  {uploadingIcon ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  ) : (
                    <>
                      <FiUploadCloud className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-xs text-gray-500">
                        Click to upload icon
                      </p>
                    </>
                  )}
                </div>
              )}

              <input
                type="file"
                accept="image/*"
                onChange={handleIconChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </div>
          </div>

          {/* FORM FIELDS */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Practice Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. Science Mock Test"
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              />
            </div>


          <div>
  <label className="block text-sm font-semibold text-gray-700 mb-1">
    Duration (Minutes)
  </label>
  <input
    type="number"
    name="durationMinutes"
    min="1"
    value={formData.durationMinutes}
    onChange={handleChange}
    placeholder="e.g. 60"
    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 
               focus:ring-2 focus:ring-blue-500 
               focus:border-transparent outline-none transition-all"
  />
</div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Total Papers
              </label>
              <input
                type="number"
                name="paper"
                min="1"
                value={formData.paper}
                onChange={handleChange}
                placeholder="e.g. 10"
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              />
            </div>
          </div>

          {/* FOOTER ACTIONS */}
          <div className="pt-4 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingIcon || !icon}
              className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-lg shadow-blue-500/30 font-medium disabled:opacity-50 disabled:shadow-none transition-all"
            >
              {loading ? "Saving..." : "Save Practice"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
